import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorDetail, errorMessage } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { date, money, percent } from '../lib/format';
import { toast } from '../lib/toast';
import {
  ASSET_WARNINGS,
  useAssetCandidates,
  useAssetKinds,
  useAssets,
  useCreateAsset,
  useDepreciationPreview,
  useDepreciationStatus,
  useDisposeAsset,
  usePostDepreciation,
  useUndisposeAsset,
  type AssetCandidate,
  type AssetKindRule,
  type FixedAsset,
} from '../lib/assets-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * DURAN VARLIKLAR VE AMORTİSMAN.
 *
 * ── EKRANIN CEVAPLADIĞI SORU ──────────────────────────────────────────────
 * "Aldığım ekipmanın bu yıl ne kadarını gider yazabilirim?" Yatırım (kutu 83)
 * alındığı yılın gideri DEĞİLDİR; maliyeti kullanım ömrü boyunca indirilir ve
 * o yıllık pay olmadan ne kâr ne de kenara ayrılacak tutar doğru çıkar.
 *
 * ── ⚠ ÜÇ ŞEY EKRANDA AÇIKÇA YAZILI OLMAK ZORUNDA ──────────────────────────
 *  1. ORAN KANUNDAN GELMİYOR. Belçika'da varlık türü başına yasal amortisman
 *     süresi yoktur; idare GERÇEK kullanım ömrüne uygun bir süre ister. Ekranda
 *     görünen yüzdeler yaygın idari toleranstır ve mükellef değiştirebilir.
 *  2. İLK YIL TAM YILDIR — çünkü mükellef GERÇEK KİŞİDİR. 2020'de pro rata
 *     temporis'i zorunlu kılan kural (WIB92 md. 196 §2) şirketler içindir.
 *  3. ELDEN ÇIKARMANIN İKİ SONUCU HESAPLANMIYOR: KDV revizyonu (KB nr. 3
 *     md. 10) ve değer artış kazancı (WIB92 md. 171, 4°).
 *
 * ⚠ OTOMATİK VARLIK ÜRETİLMİYOR. "Deftere alınmamış yatırımlar" listesi
 * hatırlatır ama oranı seçmez: oran varlığın gerçek kullanım ömrüne bağlıdır ve
 * o karar operatörün.
 */
export function AssetsPage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [creating, setCreating] = useState<AssetCandidate | 'BLANK' | null>(null);

  const assets = useAssets(year);
  const candidates = useAssetCandidates(year);
  const kinds = useAssetKinds();

  return (
    <>
      <PageHeader
        title="Duran varlıklar ve amortisman"
        description="Yatırım, alındığı yılın gideri değildir: maliyeti kullanım ömrü boyunca indirilir."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="asset-year">Yıl</label>
        <select
          id="asset-year"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-[var(--r-md)] border border-[var(--c-border-strong)] bg-[var(--c-surface)] px-2 py-1"
        >
          {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <div className="grow" />
        <Button disabled={readOnly} onClick={() => setCreating('BLANK')}>
          Varlık ekle
        </Button>
      </div>

      <DepreciationEntryCard year={year} readOnly={readOnly} />

      {candidates.isSuccess && candidates.data.length > 0 && (
        <Card className="mb-4">
          <CardHeader
            title="Deftere alınmamış yatırımlar"
            description="Onaylanmış alışlardaki kutu 83 satırları. Deftere alınmadıkça amortismanları indirilmez."
          />
          <Table>
            <thead>
              <tr>
                <Th>Belge</Th>
                <Th>Açıklama</Th>
                <Th>Aktifleşecek maliyet</Th>
                <Th>Eylem</Th>
              </tr>
            </thead>
            <tbody>
              {candidates.data.map((c) => (
                <tr key={c.purchaseInvoiceLineId}>
                  <Td>
                    {c.supplierName}
                    <span className="block text-xs text-[var(--c-ink-faint)]">
                      {c.supplierDocumentNumber} · {date(c.invoiceDate)}
                    </span>
                  </Td>
                  <Td>{c.description}</Td>
                  <Td>{money(c.capitalisedCost)}</Td>
                  <Td>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={readOnly}
                      onClick={() => setCreating(c)}
                    >
                      Deftere al
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="px-4 pb-4 text-xs text-[var(--c-ink-faint)]">
            ⚠ Oran otomatik seçilmiyor: amortisman süresi varlığın <strong>gerçek kullanım
            ömrüne</strong> göre belirlenir ve o karar sizindir. Ekrandaki öneriler yaygın idari
            toleranstır, kanun değil.
          </p>
        </Card>
      )}

      <Card>
        <CardHeader
          title={`${year} amortismanı`}
          description="İndirilebilir tutar, mesleki kullanım payı uygulanmış hâldir."
        />
        {assets.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner />
          </div>
        ) : assets.isError ? (
          <ErrorState message={errorMessage(assets.error)} detail={errorDetail(assets.error)} />
        ) : assets.data.items.length === 0 ? (
          <EmptyState
            title="Defterde varlık yok"
            description="Onaylanmış bir yatırım alışınız varsa yukarıda listelenir."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Varlık</Th>
                  <Th>Edinme</Th>
                  <Th>Maliyet</Th>
                  <Th>Oran</Th>
                  <Th>{year} amortismanı</Th>
                  <Th>Net defter değeri</Th>
                  <Th>Eylem</Th>
                </tr>
              </thead>
              <tbody>
                {assets.data.items.map((a) => (
                  <AssetRow key={a.id} asset={a} year={year} readOnly={readOnly} />
                ))}
              </tbody>
            </Table>
            <div className="border-t border-[var(--c-border)] px-4 py-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>
                  Toplam maliyet: <strong>{money(assets.data.totals.capitalisedCost)}</strong>
                </span>
                <span>
                  {year} amortismanı: <strong>{money(assets.data.totals.yearDepreciation)}</strong>
                </span>
                <span>
                  İndirilebilir: <strong>{money(assets.data.totals.yearDeductible)}</strong>
                </span>
                <span>
                  Net defter değeri: <strong>{money(assets.data.totals.bookValue)}</strong>
                </span>
              </div>
              <p className="mt-2 text-xs text-[var(--c-ink-faint)]">
                Bu tutar{' '}
                <Link className="text-[var(--c-brand)] hover:underline" to="/reports">
                  kenara ayırma hesabına
                </Link>{' '}
                gider olarak giriyor.
              </p>
            </div>
          </>
        )}
      </Card>

      {creating !== null && kinds.isSuccess && (
        <CreateAssetModal
          year={year}
          kinds={kinds.data.items}
          candidate={creating === 'BLANK' ? null : creating}
          onClose={() => setCreating(null)}
        />
      )}
    </>
  );
}

/**
 * YILIN AMORTİSMAN FİŞİ.
 *
 * ⚠ ÜÇ DURUM, ÜÇ FARKLI CÜMLE — ve hiçbiri ötekinin yerine geçmez:
 *  · yıl bitmedi → fiş atılamaz (amortisman yıl sonu kararıdır),
 *  · atılmamış → ne yazılacağı ÖNCE gösterilir, sonra atılır,
 *  · atılmış → fiş numarası ve cetvelle AYRIŞMA (varsa) gösterilir.
 *
 * ⚠ ATILMIŞ FİŞİ "DÜZELT" DÜĞMESİ YOKTUR: yasal defterin satırı sessizce
 * değiştirilemez, düzeltme yolu ters çevirmektir (yevmiye ekranında).
 */
function DepreciationEntryCard({ year, readOnly }: { year: number; readOnly: boolean }) {
  const status = useDepreciationStatus(year);
  const [showPreview, setShowPreview] = useState(false);
  const preview = useDepreciationPreview(year, showPreview);
  const post = usePostDepreciation(year);
  const [error, setError] = useState<string | null>(null);

  if (status.isPending || status.isError) return null;
  const s = status.data;
  if (s.assetCount === 0 && s.postedEntryId === null) return null;

  return (
    <Card className="mb-4">
      <CardHeader
        title={`${year} amortisman fişi`}
        description="Defterdeki kayıt: borç 630000 Amortismanlar, alacak birikmiş amortisman hesapları."
      />
      <div className="space-y-3 px-4 py-3 text-sm">
        {s.postedEntryId !== null ? (
          <>
            <p>
              Fiş atıldı: <strong>{s.postedEntryNumber ?? s.postedEntryId}</strong> ·{' '}
              {money(s.postedAmount)}
            </p>
            {s.drift !== null && s.drift !== '0.00' && (
              <p className="text-[var(--c-warn)]">
                ⚠ Cetvel ile fiş <strong>ayrıştı</strong> ({money(s.drift)}). Fiş atıldıktan sonra
                bir varlığın oranı, maliyeti ya da durumu değişmiş. Deftere yazılmış tutar geçerli
                olandır; düzeltmek isterseniz fişi{' '}
                <Link className="text-[var(--c-brand)] hover:underline" to="/accounting">
                  yevmiyeden ters çevirin
                </Link>{' '}
                ve yeniden atın.
              </p>
            )}
          </>
        ) : !s.yearEnded ? (
          <p className="text-[var(--c-ink-soft)]">
            Yıl bitmeden fiş atılmaz: amortisman <strong>yıl sonunda</strong> ayrılan bir karardır.
            Cetvele göre bu yıl ayrılacak tutar {money(s.scheduleAmount)} ({s.assetCount} varlık) —
            kenara ayırma hesabı bu rakamı zaten kullanıyor.
          </p>
        ) : (
          <>
            <p>
              Deftere yazılacak: <strong>{money(s.scheduleAmount)}</strong> ({s.assetCount} varlık)
              {s.scheduleDeductible !== s.scheduleAmount && (
                <span className="text-[var(--c-ink-soft)]">
                  {' '}
                  · vergide indirilebilen {money(s.scheduleDeductible)}
                </span>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? 'Önizlemeyi gizle' : 'Fişi önizle'}
              </Button>
              <Button
                disabled={readOnly || !s.postable || post.isPending}
                onClick={() => {
                  setError(null);
                  post.mutate(undefined, {
                    onSuccess: () => toast.success('Amortisman fişi deftere atıldı.'),
                    onError: (e: unknown) => setError(errorMessage(e)),
                  });
                }}
              >
                Fişi at
              </Button>
            </div>

            {showPreview && preview.isSuccess && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--c-ink-faint)]">
                    <th className="py-1 text-left font-medium">Hesap</th>
                    <th className="py-1 text-left font-medium">Açıklama</th>
                    <th className="py-1 text-left font-medium">Borç</th>
                    <th className="py-1 text-left font-medium">Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.lines.map((l, i) => (
                    <tr key={`${l.accountCode}-${String(i)}`}>
                      <td className="py-0.5">{l.accountCode}</td>
                      <td className="py-0.5">{l.description}</td>
                      <td className="py-0.5">{l.debit === '0.00' ? '—' : money(l.debit)}</td>
                      <td className="py-0.5">{l.credit === '0.00' ? '—' : money(l.credit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {showPreview && preview.isError && (
              <p className="text-[var(--c-danger)]">{errorMessage(preview.error)}</p>
            )}
          </>
        )}

        {/* ⚠ Defterdeki tutar TAMDIR; özel kullanım payı bir vergi
            düzeltmesidir ve fiş satırının açıklamasında yazar. */}
        <p className="text-xs text-[var(--c-ink-faint)]">
          ⚠ Deftere <strong>tam</strong> amortisman yazılır; özel kullanım payı bir muhasebe kaydı
          değil, vergi düzeltmesidir ve fiş satırının açıklamasında görünür.
        </p>

        {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}
      </div>
    </Card>
  );
}

function AssetRow({
  asset,
  year,
  readOnly,
}: {
  asset: FixedAsset;
  year: number;
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [disposing, setDisposing] = useState(false);
  const undispose = useUndisposeAsset(year);

  return (
    <>
      <tr>
        <Td>
          <button type="button" className="text-left hover:underline" onClick={() => setOpen(!open)}>
            {asset.description}
          </button>
          <span className="block text-xs text-[var(--c-ink-faint)]">
            {asset.assetKindLabel}
            {asset.disposalDate !== null && ` · elden çıkarıldı ${date(asset.disposalDate)}`}
          </span>
        </Td>
        <Td>{date(asset.acquisitionDate)}</Td>
        <Td>
          {money(asset.capitalisedCost)}
          {asset.capitalisedNonDeductibleVat !== '0.00' && (
            <span className="block text-xs text-[var(--c-ink-faint)]">
              {money(asset.capitalisedNonDeductibleVat)} indirilemeyen KDV dâhil
            </span>
          )}
        </Td>
        <Td>
          {percent(asset.annualRatePct)}
          <span className="block text-xs text-[var(--c-ink-faint)]">
            {asset.firstYearBasis === 'FULL_YEAR' ? 'ilk yıl tam' : 'ilk yıl pro rata'}
            {asset.businessUsePct !== '100.00' && ` · mesleki %${asset.businessUsePct}`}
          </span>
        </Td>
        <Td>
          {money(asset.yearDepreciation)}
          {asset.yearDeductible !== asset.yearDepreciation && (
            <span className="block text-xs text-[var(--c-ink-faint)]">
              indirilebilir: {money(asset.yearDeductible)}
            </span>
          )}
        </Td>
        <Td>{money(asset.bookValue)}</Td>
        <Td>
          {asset.disposalDate === null ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={readOnly}
              onClick={() => setDisposing(true)}
            >
              Elden çıkar
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={readOnly || undispose.isPending}
              onClick={() =>
                undispose.mutate(asset.id, {
                  onSuccess: () => toast.success('Elden çıkarma geri alındı.'),
                  onError: (e: unknown) => toast.error(e),
                })
              }
            >
              Geri al
            </Button>
          )}
        </Td>
      </tr>

      {open && (
        <tr>
          <Td colSpan={7}>
            <div className="space-y-3 py-2">
              <div>
                <p className="mb-1 text-xs font-medium text-[var(--c-ink-soft)]">
                  Amortisman cetveli
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[var(--c-ink-faint)]">
                      <th className="py-1 text-left font-medium">Yıl</th>
                      <th className="py-1 text-left font-medium">Amortisman</th>
                      <th className="py-1 text-left font-medium">İndirilebilir</th>
                      <th className="py-1 text-left font-medium">Birikmiş</th>
                      <th className="py-1 text-left font-medium">Net değer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asset.schedule.map((y) => (
                      <tr key={y.fiscalYear} className={y.fiscalYear === year ? 'font-medium' : ''}>
                        <td className="py-0.5">
                          {y.fiscalYear}
                          {y.proRataDays !== null && ` (${y.proRataDays} gün)`}
                        </td>
                        <td className="py-0.5">{money(y.amount)}</td>
                        <td className="py-0.5">{money(y.deductible)}</td>
                        <td className="py-0.5">{money(y.accumulated)}</td>
                        <td className="py-0.5">{money(y.bookValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="space-y-1 text-xs">
                {asset.warnings.map((code) => (
                  <li key={code} className="flex gap-2">
                    <Badge tone={code === 'DISPOSAL_CONSEQUENCES_NOT_MODELLED' ? 'warn' : 'neutral'}>
                      !
                    </Badge>
                    <span>{ASSET_WARNINGS[code] ?? code}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Td>
        </tr>
      )}

      {disposing && (
        <DisposeModal asset={asset} year={year} onClose={() => setDisposing(false)} />
      )}
    </>
  );
}

function DisposeModal({
  asset,
  year,
  onClose,
}: {
  asset: FixedAsset;
  year: number;
  onClose: () => void;
}) {
  const dispose = useDisposeAsset(year);
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().slice(0, 10));
  const [proceeds, setProceeds] = useState('0.00');
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      open
      onClose={onClose}
      title="Varlığı elden çıkar"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            disabled={dispose.isPending}
            onClick={() => {
              setError(null);
              dispose.mutate(
                { id: asset.id, body: { disposalDate, proceedsExclVat: proceeds } },
                {
                  onSuccess: () => {
                    toast.success('Varlık elden çıkarıldı; amortismanı durdu.');
                    onClose();
                  },
                  onError: (e: unknown) => setError(errorMessage(e)),
                },
              );
            }}
          >
            Elden çıkar
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Input
          label="Elden çıkarma tarihi"
          type="date"
          value={disposalDate}
          onChange={(e) => setDisposalDate(e.target.value)}
        />
        <Input
          label="Satış bedeli (KDV hariç)"
          hint="Hurdaya ayrıldıysa 0 bırakın."
          value={proceeds}
          onChange={(e) => setProceeds(e.target.value)}
        />

        {/* ⚠ HESAPLANMAYAN ŞEYİ HESAPLANMIŞ GİBİ GÖSTERMEMEK: iki sonuç da
            burada AÇIKÇA yazılı. */}
        <p className="text-xs text-[var(--c-ink-faint)]">
          ⚠ Bu işlem yalnız <strong>amortismanı durdurur</strong>. Elden çıkarmanın iki vergisel
          sonucu bu yazılımda <strong>hesaplanmıyor</strong>: revizyon dönemi içindeyse{' '}
          <strong>KDV revizyonu</strong> (KB nr. 3 md. 10 — kutu 61/62) ve{' '}
          <strong>değer artış kazancı</strong> (WIB92 md. 171, 4°). Varlığın KDV revizyon dönemi{' '}
          {asset.vatRevisionYears} yıldır ve {date(asset.acquisitionDate)} tarihinde başlamıştır.
        </p>

        {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}
      </div>
    </Modal>
  );
}

function CreateAssetModal({
  year,
  kinds,
  candidate,
  onClose,
}: {
  year: number;
  kinds: AssetKindRule[];
  candidate: AssetCandidate | null;
  onClose: () => void;
}) {
  const create = useCreateAsset(year);
  const [assetKind, setAssetKind] = useState<AssetKindRule>(
    kinds.find((k) => k.key === 'IT_EQUIPMENT') ?? kinds[0]!,
  );
  const [description, setDescription] = useState(candidate?.description ?? '');
  const [acquisitionDate, setAcquisitionDate] = useState(
    candidate?.invoiceDate ?? new Date().toISOString().slice(0, 10),
  );
  const [cost, setCost] = useState(candidate?.capitalisedCost ?? '');
  const [ratePct, setRatePct] = useState(assetKind.suggestedRatePct);
  const [businessUsePct, setBusinessUsePct] = useState(candidate?.incomeTaxDeductiblePct ?? '100');
  const [firstYearBasis, setFirstYearBasis] = useState<'FULL_YEAR' | 'PRO_RATA'>('FULL_YEAR');
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      open
      onClose={onClose}
      title={candidate ? 'Yatırımı deftere al' : 'Varlık ekle'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            disabled={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate(
                {
                  description,
                  assetKind: assetKind.key,
                  acquisitionDate,
                  acquisitionCostExclVat: cost,
                  annualRatePct: ratePct,
                  businessUsePct,
                  firstYearBasis,
                  ...(candidate
                    ? {
                        source: 'PURCHASE',
                        purchaseInvoiceId: candidate.purchaseInvoiceId,
                        purchaseInvoiceLineId: candidate.purchaseInvoiceLineId,
                      }
                    : {}),
                },
                {
                  onSuccess: () => {
                    toast.success('Varlık deftere alındı.');
                    onClose();
                  },
                  onError: (e: unknown) => setError(errorMessage(e)),
                },
              );
            }}
          >
            Kaydet
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Input
          label="Açıklama"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <Select
          label="Sınıf"
          value={assetKind.key}
          onChange={(e) => {
            const next = kinds.find((k) => k.key === e.target.value) ?? assetKind;
            setAssetKind(next);
            // ⚠ Sınıf değişince ÖNERİ değişir; operatör isterse üstüne yazar.
            setRatePct(next.suggestedRatePct);
          }}
        >
          {kinds.map((k) => (
            <option key={k.key} value={k.key}>
              {k.label}
            </option>
          ))}
        </Select>

        <p className="text-xs text-[var(--c-ink-faint)]">{assetKind.note}</p>

        <Input
          label="Edinme tarihi"
          type="date"
          value={acquisitionDate}
          onChange={(e) => setAcquisitionDate(e.target.value)}
        />

        <Input
          label="Maliyet (KDV hariç + indirilemeyen KDV)"
          hint={
            candidate
              ? 'Alış satırından geldi: matrah + satıra düşen indirilemeyen KDV.'
              : 'İndirilemeyen KDV varsa maliyete dâhil edin.'
          }
          value={cost}
          onChange={(e) => setCost(e.target.value)}
        />

        <Input
          label="Yıllık amortisman oranı (%)"
          hint="⚠ Kanunda yazmaz: idari tolerans. Varlığın GERÇEK kullanım ömrüne göre değiştirin."
          value={ratePct}
          onChange={(e) => setRatePct(e.target.value)}
        />

        <Input
          label="Mesleki kullanım oranı (%)"
          hint="Özel kullanım payı vergiden indirilmez."
          value={businessUsePct}
          onChange={(e) => setBusinessUsePct(e.target.value)}
        />

        <Select
          label="İlk yıl"
          hint="⚠ Gerçek kişide TAM YIL hakkınızdır; pro rata zorunluluğu şirketler içindir (WIB92 md. 196 §2)."
          value={firstYearBasis}
          onChange={(e) => setFirstYearBasis(e.target.value as 'FULL_YEAR' | 'PRO_RATA')}
        >
          <option value="FULL_YEAR">Tam yıl</option>
          <option value="PRO_RATA">Gün esaslı (pro rata)</option>
        </Select>

        {error !== null && <p className="text-[var(--c-danger)]">{error}</p>}
      </div>
    </Modal>
  );
}
